import { i18n } from '@lingui/core'
import { detect, fromNavigator, fromStorage } from '@lingui/detect-locale'
import type { Locale } from '@skyground/contracts'
import { supportedLocales } from '@skyground/contracts'
import invariant from 'tiny-invariant'

import { defaultLocale, loadCatalog, LOCALE_STORAGE_KEY } from './constants.js'

const isValidLocale = (value: unknown): value is Locale => supportedLocales.includes(value as Locale)

/**
 * Reduce a BCP-47 tag to the bare language subtag we actually support (`vi-VN` → `vi`, `pt_BR` →
 * `pt`). `supportedLocales` holds bare two/three-letter codes only, while `fromNavigator()` hands
 * back `navigator.language` verbatim — which is region-tagged on virtually every real device. Without
 * this, a Vietnamese phone reporting `vi-VN` failed the exact-match validity check and silently fell
 * through to English (same for `de-DE`, `pt-BR`, …).
 */
export const toBaseLocale = (tag: null | string | undefined): string | undefined => tag?.split(/[-_]/)[0]?.toLowerCase()

/**
 * Load and activate a locale on the shared lingui instance, persisting the choice. Async because a
 * locale's catalog is a lazily-imported chunk (see {@link loadCatalog}); until it resolves, lingui
 * falls back to the English source strings baked into the macros, so text is always readable.
 */
export const dynamicActivate = async (locale: Locale): Promise<void> => {
  invariant(isValidLocale(locale))

  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch (error) {
    console.error('LocalStorage is not available', error)
  }

  const messages = await loadCatalog(locale)
  i18n.load(locale, messages)
  i18n.activate(locale)
}

/** Resolve the locale to show first: a stored preference, then the device language, else the default. */
export const getInitialLocale = (): Locale => {
  try {
    const detected = detect(fromStorage(LOCALE_STORAGE_KEY), fromNavigator(), () => defaultLocale)
    // Region-tagged device languages must be narrowed to their base subtag before validation —
    // see {@link toBaseLocale}.
    const locale = toBaseLocale(detected)
    if (isValidLocale(locale)) return locale
  } catch (error) {
    console.error('LocalStorage is not available', error)
  }

  return defaultLocale
}
